/** 分包工作页展示口径：合同、任务摘要和历史线下合同共用状态，不改签署或付款的业务条件。
 * 页面不能把上传文件冒充电子签署，也不能把未付合同余额当作当前可付款金额。
 */
export const SUB_SECTIONS = [
  ['contract', '合同与报价', 'Contract & quotes'],
  ['construction', '施工与验收', 'Construction'],
  ['payments', '付款与变更', 'Payments & changes'],
  ['records', '资料与记录', 'Files & activity'],
];

/** 分包管理的合同、询价和待安排工种共用项目任务链顺序，避免接口插入顺序造成列表杂乱。
 * @param {Object} nodeTrades 当前项目已合并全局及项目覆盖的节点→工种映射。
 * @param {Array} trades 标准工种目录；同节点工种及未关联任务的工种沿用目录顺序。
 * @returns {Function} 用于列表副本的比较函数，不修改接口数据或合同业务状态。
 * 同工种跨多个阶段时取最早关联节点；无关联工种置后，同工种多记录按创建时间、ID 固定排序。
 */
export function subcontractProjectOrder(nodeTrades = {}, trades = []) {
  const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), 'en', { numeric: true });
  const firstNode = new Map(), catalogOrder = new Map();
  Object.keys(nodeTrades).sort(compareText).forEach((node, index) => {
    (nodeTrades[node]?.trades || []).forEach(key => {
      if (!firstNode.has(key)) firstNode.set(key, index);
    });
  });
  trades.forEach((trade, index) => {
    if (!catalogOrder.has(trade.tradeKey)) catalogOrder.set(trade.tradeKey, index);
  });
  const rank = (map, key) => map.get(key) ?? Number.MAX_SAFE_INTEGER;
  return (a, b) => {
    // 询价使用 trade 字段，合同与工种使用 tradeKey；三个分类必须按同一业务工种比较。
    const aKey = a.tradeKey || a.trade || '', bKey = b.tradeKey || b.trade || '';
    return rank(firstNode, aKey) - rank(firstNode, bKey)
      || rank(catalogOrder, aKey) - rank(catalogOrder, bKey)
      || compareText(aKey, bKey)
      || compareText(a.createdAt, b.createdAt)
      || compareText(a.id, b.id);
  };
}

/** 兼容通知、项目任务卡和历史付款节点深链；仅转换展示分区，原节点参数继续保留。 */
export function subcontractSection(section) {
  if (['tasks', 'startwork', 'stages', 'rects', 'schedule', 'construction'].includes(section)) return 'construction';
  if (['payments', 'payplan', 'payment', 'change-orders', 'change_orders'].includes(section)) return 'payments';
  if (['docs', 'photos', 'timeline', 'records', 'completion'].includes(section)) return 'records';
  return 'contract';
}

/** 兼容完整合同与 trade-tasks 摘要。发送待签优先于已有文件，撤销合同不计入有效合同。 */
export function subcontractStatus(sc = {}, zh = true) {
  const make = (key, label, en, valid = false) => ({ key, label: zh ? label : en, valid });
  if (sc.status === 'cancelled') return make('cancelled', '已取消', 'Cancelled');
  if (sc.esignStatus === 'sent') return make('pending', '待签署', 'Awaiting signature');
  if (sc.esignStatus === 'signed') return make('signed', '电子签署完成', 'Electronically signed', true);
  if (sc.contractFile?.fileId || sc.hasContractFile) return make('offline', '线下合同已上传', 'Offline contract uploaded', true);
  if (sc.status === 'signed') return make('signed', '已签署', 'Signed', true);
  return make('draft', '待完善合同', 'Draft contract');
}

/** 已批准变更已经进入付款计划，仅累计 change_order 节点一次；余额允许为负以暴露超付。 */
export function subcontractAmounts(sc = {}) {
  const plan = sc.paymentPlan || sc.milestones || [];
  const contract = Number(sc.contractAmount || 0);
  const changes = plan.filter(m => m.kind === 'change_order').reduce((sum, m) => sum + Number(m.amount || 0), 0);
  const paid = plan.filter(m => m.status === 'paid').reduce((sum, m) => sum + Number(m.amount || 0), 0);
  return { contract, changes, paid, remaining: Math.round((contract + changes - paid) * 100) / 100 };
}

/** 施工状态来自现场阶段或开工确认，关联任务完成比例不作为实际工程进度。 */
export function subcontractConstruction(sc = {}, summary, zh = true) {
  const linked = summary && summary.id === sc.id ? summary : null;
  const plan = sc.paymentPlan || linked?.milestones || [];
  const current = linked?.current || plan.find(m => m.stageStatus && m.stageStatus !== 'closed');
  const sw = sc.startWork || {};
  const states = {
    pending: ['待安排', 'Not arranged'], notified: ['待确认工期', 'Awaiting schedule'],
    in_progress: ['施工中', 'In progress'], started: ['施工中', 'In progress'],
    completed_pending: ['待验收', 'Awaiting inspection'], reported: ['待验收', 'Awaiting inspection'], completed: ['待验收', 'Awaiting inspection'],
    inspecting: ['验收中', 'Inspecting'], closed: ['已验收', 'Accepted'],
  };
  let key = current?.stageStatus || 'pending';
  if (sw.status === 'reschedule_requested') return { key: 'reschedule', label: zh ? '申请改期' : 'Reschedule requested' };
  if (key === 'pending' && plan.length && plan.every(m => m.stageStatus === 'closed')) key = 'closed';
  if (key === 'pending' && sw.plannedDate) return { key: sw.status === 'confirmed' ? 'confirmed' : 'start_pending', label: zh ? (sw.status === 'confirmed' ? '开工已确认' : '开工待确认') : (sw.status === 'confirmed' ? 'Start confirmed' : 'Start pending') };
  return { key, label: (states[key] || states.pending)[zh ? 0 : 1] };
}

/** 与付款计划审批标签共用角色口径；同工种有多份合同时不能只查工种摘要中的一份。 */
export function subcontractPaymentNeedsReview(req, role) {
  const roles = { submitted: ['pm', 'admin', 'ceo'], pm_approved: ['eng_manager', 'admin', 'ceo'], finance_pending: ['finance', 'cashier', 'admin', 'ceo'], finance_approved: ['finance', 'cashier', 'admin', 'ceo'], check_issued: ['finance', 'cashier', 'admin', 'ceo'] };
  return !!roles[req?.status]?.includes(role);
}

/** 付款只有本人岗位可以审批才列为“待我处理”；通用管理权限不等于个人待办。 */
export function subcontractNextAction(sc, summary, canManage, zh = true, requests = [], role = '') {
  const linked = summary?.id === sc.id ? summary : null;
  const pay = requests.find(r => r.subcontractId === sc.id && subcontractPaymentNeedsReview(r, role)) || linked?.pendingPayItems?.find(r => r.mine);
  const make = (label, en, section, mine = false, milestone = '') => ({ label: zh ? label : en, section, mine, milestone });
  if (pay || linked?.pendingPay > 0) return make('处理付款', 'Review payment', 'payments', true, pay?.milestoneId);
  const stage = subcontractConstruction(sc, linked, zh);
  if (canManage && stage.key === 'reschedule') return make('处理改期', 'Review reschedule', 'construction');
  if (canManage && ['completed_pending', 'reported', 'completed', 'inspecting'].includes(stage.key)) return make('处理验收', 'Review inspection', 'construction');
  const contract = subcontractStatus(sc, zh);
  if (!contract.valid) return make(contract.key === 'pending' ? '查看签署' : canManage ? '完善合同' : '查看合同', contract.key === 'pending' ? 'View signing' : canManage ? 'Complete contract' : 'View contract', 'contract');
  if (canManage && !sc.startWork?.plannedDate) return make('安排开工', 'Arrange start', 'construction');
  return make('查看进度', 'View progress', 'construction');
}

/** 工种展示随界面语言选择；只裁剪双语工种名，不改合同原文及自定义名称。 */
export function subcontractTradeName(name = '', zh = true, tradeKey = '') {
  if (!zh || tradeKey === 'custom' || !/[\u3400-\u9fff]/.test(name)) return name;
  return String(name).replace(/\s+[A-Za-z][A-Za-z0-9 &/.-]*/g, '').replace(/\s*\(\s*/g, '（').replace(/\s*\)/g, '）').trim() || name;
}

export function subcontractUrl(projectId, sub = '', section = '', milestone = '') {
  const query = new URLSearchParams();
  if (sub) query.set('sub', sub);
  if (section) query.set('section', section);
  if (milestone) query.set('ms', milestone);
  return `/project/${encodeURIComponent(projectId)}/subcontracts${query.toString() ? `?${query}` : ''}`;
}

/** 旧项目路径会被全局抽屉拦截并携带当前深链，直接返回它会再次进入分包页。
 * 项目直链及分包页自身统一回到看板项目抽屉；其他来源保留其列表筛选并摘除分包定位参数。
 */
export function subcontractReturnUrl(pathname, search, projectId) {
  if (/^\/project\/[^/]+(?:\/subcontracts)?\/?$/.test(pathname)) return `/board?project=${encodeURIComponent(projectId)}`;
  const query = new URLSearchParams(search);
  ['sub', 'section', 'ms', 'inspect', 'highlight', 'rfq'].forEach(k => query.delete(k));
  return pathname + (query.toString() ? '?' + query : '');
}
